/**
 * AI Recruit - Resume Info Tooltip
 * Hover tooltip for the Candidate Management table's "(i)" upload/processing
 * timing icon. Portals into document.body and clamps to the viewport instead
 * of relying on plain CSS absolute positioning, so it can't get clipped or
 * spill into neighboring table rows/columns the way a fixed-offset tooltip
 * does once rows are this compact — same technique used on the Job
 * Management table's info tooltip.
 */

import React, { useState, useRef } from 'react'
import { createPortal } from 'react-dom'

interface ResumeInfoTooltipProps {
  content: React.ReactNode
  children: React.ReactElement
  panelWidth?: number
}

const MARGIN = 8

const ResumeInfoTooltip: React.FC<ResumeInfoTooltipProps> = ({ content, children, panelWidth = 260 }) => {
  const [isVisible, setIsVisible] = useState(false)
  const [position, setPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 })
  const triggerRef = useRef<HTMLSpanElement>(null)
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const computePosition = () => {
    const trigger = triggerRef.current
    if (!trigger) return
    const rect = trigger.getBoundingClientRect()
    const estimatedHeight = 220
    const openUpward = rect.bottom + estimatedHeight + MARGIN > window.innerHeight

    let left = rect.left
    left = Math.min(Math.max(left, MARGIN), window.innerWidth - panelWidth - MARGIN)

    const top = openUpward ? Math.max(rect.top - estimatedHeight - 8, MARGIN) : rect.bottom + 8

    setPosition({ top, left })
  }

  const show = () => {
    if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current)
    computePosition()
    setIsVisible(true)
  }

  const hide = () => {
    hideTimeoutRef.current = setTimeout(() => setIsVisible(false), 50)
  }

  return (
    <span ref={triggerRef} onMouseEnter={show} onMouseLeave={hide} className="inline-flex">
      {children}
      {isVisible &&
        createPortal(
          <div
            className="fixed z-50 p-3 bg-white border border-gray-200 rounded-md shadow-lg pointer-events-none"
            style={{ top: position.top, left: position.left, width: panelWidth }}
          >
            {content}
          </div>,
          document.body
        )}
    </span>
  )
}

export default ResumeInfoTooltip
